'use client';

import { useClickOutside, useHotkeys } from '@mantine/hooks';
import Image from 'next/image';
import { useImperativeHandle, useState, type Ref } from 'react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa6';
import { Keyboard } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { twJoin } from 'tailwind-merge';

import { ImagesModalControlButton } from './ImagesModalControlButton';

import 'swiper/css';

const imageSize = { height: 750, width: 1125 } as const;

export type ImagesModalRef = {
  open(index?: number): void;
};

export type ImagesModalProps = {
  images: ZmyleImage[];
  ref?: Ref<ImagesModalRef>;
};

export function ImagesModal({ images, ref }: ImagesModalProps) {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const imagesAdjustedSizes = images.map(image => {
    const url = new URL(image.url);

    url.searchParams.set('height', String(imageSize.height));
    url.searchParams.set('width', String(imageSize.width));
    url.searchParams.set('mode', 'fit');

    return { ...image, url: url.href };
  });

  function onSwiperChange(_swiper: SwiperClass) {
    setSwiper(_swiper);
  }

  function onIsOpenChange(_isOpen: boolean) {
    if (_isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    setIsOpen(_isOpen);
  }

  useImperativeHandle(ref, () => ({
    open(index?: number) {
      swiper?.slideTo(index ?? 0);

      onIsOpenChange(true);
    },
  }));

  function onPrevImage() {
    swiper?.slidePrev();
  }

  function onNextImage() {
    swiper?.slideNext();
  }

  useHotkeys([
    [
      'Escape',
      () => {
        if (isOpen) onIsOpenChange(false);
      },
    ],
  ]);

  const contentRef = useClickOutside<HTMLDivElement>(() => {
    onIsOpenChange(false);
  });

  return (
    <div
      data-state={isOpen ? 'open' : 'closed'}
      className={twJoin(
        'fixed inset-0 z-[11] grid place-content-center bg-[rgba(14,38,69,0.77)]',
        'data-[state=open]:animate-dialog-overlay-open',
        'data-[state=closed]:hidden',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="Images View Modal"
    >
      <div
        className={twJoin([
          'fixed top-1/2 right-1/2 z-[12] translate-x-1/2 -translate-y-1/2',
          'align-center flex h-full w-4xl max-w-[80vw] flex-col justify-center',
          'outline-none',
          'data-[state=open]:animate-dialog-content-open',
          'data-[state=closed]:hidden',
        ])}
      >
        <div ref={contentRef} className="relative rounded-md">
          <ImagesModalControlButton
            className="absolute top-1/2 left-0 z-[13] -translate-x-1/2 -translate-y-1/2"
            aria-label="Previous image"
            onClick={onPrevImage}
          >
            <span className="sr-only">Previous image</span>

            <FaArrowLeft className="size-6 text-blue-800" />
          </ImagesModalControlButton>

          <ImagesModalControlButton
            className="absolute top-1/2 right-0 z-[13] translate-x-1/2 -translate-y-1/2"
            aria-label="Next image"
            onClick={onNextImage}
          >
            <span className="sr-only">Next image</span>

            <FaArrowRight className="size-6 text-blue-800" />
          </ImagesModalControlButton>

          <Swiper
            onSwiper={onSwiperChange}
            direction="horizontal"
            slidesPerView={1}
            spaceBetween={32}
            modules={[Keyboard]}
            autoHeight
            rewind
            keyboard
          >
            {imagesAdjustedSizes.map((image, index) => {
              return (
                <SwiperSlide
                  key={index}
                  className="bg-dynamic-highlight self-center"
                >
                  <Image
                    src={image.url}
                    className="rounded-md"
                    height={imageSize.height}
                    width={imageSize.width}
                    alt={String(index + 1)}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </div>
  );
}
